// Lógica pura do canvas de Automações (sem React): auto-conexão ao soltar um
// nó e alinhamento em camadas. Fica separada pra ser testável com node --test.

export const STEP_Y = 150 // distância vertical entre camadas
export const STEP_X = 300 // largura do nó (250) + respiro entre irmãos

/**
 * Primeira saída do nó de origem ainda sem aresta. `handles` vem de
 * nodeHandles(): array de { id } ou null (nó de saída única, sem handle nomeado).
 * Retorna { handle } (handle = null pra saída única) ou null se não sobrou saída.
 */
export function firstFreeOutput(sourceId, handles, edges) {
  const usadas = edges.filter((e) => e.source === sourceId)
  if (!handles?.length) return usadas.length ? null : { handle: null }
  const livre = handles.find((h) => !usadas.some((e) => e.sourceHandle === h.id))
  return livre ? { handle: livre.id } : null
}

/**
 * Posições em camadas seguindo as conexões (raízes no topo, fluxo cresce em Y).
 * A camada de um nó é o maior caminho até ele (ignora arestas de retorno, então
 * ciclos não travam). Nós de uma mesma camada ficam lado a lado, ordenados pela
 * ordem de visita em profundidade (respeita a ordem das saídas) e centralizados
 * em x=0. Retorna { [id]: { x, y } } — só posições, arestas não são tocadas.
 */
export function layoutLayers(nodes, edges) {
  const ids = new Set(nodes.map((n) => n.id))
  const out = new Map(nodes.map((n) => [n.id, []]))
  const hasIn = new Set()
  edges.forEach((e) => {
    if (!ids.has(e.source) || !ids.has(e.target) || e.source === e.target) return
    out.get(e.source).push(e.target)
    hasIn.add(e.target)
  })

  // Visita em profundidade (raízes primeiro; componente só de ciclo começa por
  // qualquer nó). Guarda a ordem de visita e as arestas de retorno.
  const order = []
  const state = new Map() // 1 = na pilha, 2 = concluído
  const back = new Set() // arestas de retorno "src>dst"
  const visit = (start) => {
    const stack = [[start, 0]]
    state.set(start, 1)
    order.push(start)
    while (stack.length) {
      const top = stack[stack.length - 1]
      const [id, i] = top
      const filhos = out.get(id)
      if (i >= filhos.length) { state.set(id, 2); stack.pop(); continue }
      top[1] += 1
      const t = filhos[i]
      if (state.get(t) === 1) back.add(`${id}>${t}`)
      else if (!state.has(t)) { state.set(t, 1); order.push(t); stack.push([t, 0]) }
    }
  }
  nodes.filter((n) => !hasIn.has(n.id)).forEach((n) => { if (!state.has(n.id)) visit(n.id) })
  nodes.forEach((n) => { if (!state.has(n.id)) visit(n.id) })

  // Maior caminho no grafo sem arestas de retorno (DAG): ordem topológica (Kahn).
  const dag = new Map(nodes.map((n) => [n.id, []]))
  const indeg = new Map(nodes.map((n) => [n.id, 0]))
  out.forEach((ts, s) => ts.forEach((t) => {
    if (back.has(`${s}>${t}`)) return
    dag.get(s).push(t)
    indeg.set(t, indeg.get(t) + 1)
  }))
  const depth = new Map(nodes.map((n) => [n.id, 0]))
  const fila = nodes.filter((n) => indeg.get(n.id) === 0).map((n) => n.id)
  while (fila.length) {
    const id = fila.shift()
    dag.get(id).forEach((t) => {
      depth.set(t, Math.max(depth.get(t), depth.get(id) + 1))
      indeg.set(t, indeg.get(t) - 1)
      if (indeg.get(t) === 0) fila.push(t)
    })
  }

  const layers = []
  order.forEach((id) => {
    const d = depth.get(id)
    if (!layers[d]) layers[d] = []
    layers[d].push(id)
  })
  const pos = {}
  layers.forEach((camada, d) => {
    if (!camada) return
    const w = (camada.length - 1) * STEP_X
    camada.forEach((id, i) => { pos[id] = { x: i * STEP_X - w / 2, y: d * STEP_Y } })
  })
  return pos
}
